import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Lock, 
  MessageSquare, 
  TrendingUp, 
  LayoutGrid,
  ChevronDown,
  Calendar,
  Users
} from 'lucide-react';
import api from '../api/client';
import clsx from 'clsx';
import { Select } from 'antd';

const DashboardCard = ({ title, value, subValue, icon: Icon, extra }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.98 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.2 }}
    className="bg-surface rounded-xl p-5 border border-white/5 relative overflow-hidden flex flex-col justify-between h-[160px]"
  >
    <div className="flex justify-between items-start mb-2">
      <h3 className="text-sm font-medium text-slate-200">{title}</h3>
      {Icon && <Icon size={16} className="text-slate-500" />}
    </div>
    
    <div className="mt-auto">
      <div className="text-3xl font-bold text-white mb-2">{value}</div>
      {subValue && (
        <div className="text-xs text-slate-500 font-medium">{subValue}</div>
      )}
    </div>
    {extra}
  </motion.div>
);

const EmptyCard = ({ title, rightMetric, subtitle }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.98 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.2 }}
    className="bg-surface rounded-xl p-5 border border-white/5 h-[280px] flex flex-col"
  >
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-medium text-slate-200">{title}</h3>
      {rightMetric && (
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{rightMetric}</div>
          <div className="text-[10px] text-slate-500 uppercase font-bold">{subtitle}</div>
        </div>
      )}
    </div>
    <div className="flex-1 flex items-center justify-center">
       {/* Placeholder for empty state chart/list */}
       <div className="w-full h-full bg-white/[0.02] rounded-lg border border-white/5 border-dashed flex items-center justify-center text-slate-600 text-xs">
          Нет данных
       </div>
    </div>
  </motion.div>
);

const ListCard = ({ title, items, total, subtitle }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.98 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.2 }}
    className="bg-surface rounded-xl p-5 border border-white/5 h-[280px] flex flex-col"
  >
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-medium text-slate-200">{title}</h3>
      <div className="text-right">
        <div className="text-2xl font-bold text-white">{total}</div>
        <div className="text-[10px] text-slate-500 uppercase font-bold">{subtitle}</div>
      </div>
    </div>
    <div className="flex-1 overflow-y-auto pr-2">
       {items && items.length > 0 ? (
         <div className="space-y-3">
           {items.map((item, index) => (
             <div key={index} className="flex items-center justify-between">
               <div className="flex items-center gap-3">
                 <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-400 text-xs font-bold">
                   {index + 1}
                 </div>
                 <span className="text-sm text-slate-200">{item.label}</span>
               </div>
               <div className="text-sm font-medium text-white">
                 {item.value} <span className="text-xs text-slate-500">({item.percent}%)</span>
               </div>
             </div>
           ))}
         </div>
       ) : (
         <div className="w-full h-full bg-white/[0.02] rounded-lg border border-white/5 border-dashed flex items-center justify-center text-slate-600 text-xs">
            Нет данных
         </div>
       )}
    </div>
  </motion.div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      setData(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = data?.revenue?.total_revenue || 0;
  const avgPayment = Number(data?.revenue?.avg_payment || 0).toFixed(0);
  
  // Use real channel member count if available, otherwise fall back to source tracking
  const channelMembers = data?.channels?.reduce((sum, c) => sum + (parseInt(c.member_count) || 0), 0) || 0;
  const trackedSubscribers = data?.sourceStats?.reduce((sum, s) => sum + parseInt(s.subscribed), 0) || 0;
  
  const totalSubscribers = channelMembers > 0 ? channelMembers : trackedSubscribers;

  // Prepare Channel Stats for fallback
  const totalUsers = data?.sourceStats?.reduce((sum, s) => sum + parseInt(s.count), 0) || 0;
  const topSources = data?.sourceStats
    ?.sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map(s => ({
      label: s.source,
      value: s.count,
      percent: totalUsers > 0 ? Math.round((s.count / totalUsers) * 100) : 0
    })) || [];

  const channelStatsItems = data?.channels?.map(c => ({
    label: c.channel_name,
    value: c.member_count,
    percent: totalSubscribers > 0 ? Math.round((c.member_count / totalSubscribers) * 100) : 0
  })) || [];

  // Determine what to show in the list card
  const showChannels = topSources.length === 0 && channelStatsItems.length > 0;
  const listItems = showChannels ? channelStatsItems : topSources;
  const listTitle = showChannels ? "Статистика каналов" : "Топ источников";
  const listTotal = showChannels ? totalSubscribers : totalUsers;
  const listSubtitle = showChannels ? "Всего подписчиков" : "Всего переходов";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-white">Дашборд</h1>
        <button className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium text-white flex items-center gap-2 transition-colors">
          <LayoutGrid size={16} />
          Добавить виджет
        </button>
      </div>

      {/* Top Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <DashboardCard 
          title="Выручка" 
          value={`${totalRevenue} $`} 
          subValue="В среднем/мес: —"
          icon={FileText} 
        />
        <DashboardCard 
          title="Продажи" 
          value="0" 
          subValue={
            <div className="flex items-center gap-1">
              Повторных: <span className="text-white">0</span>
            </div>
          }
          icon={Lock} 
        />
        <DashboardCard 
          title="Новые диалоги" 
          value="0" 
          subValue="Боты: 0 | ЛС: 0"
          icon={MessageSquare} 
        />
        <DashboardCard 
          title="Всего подписчиков" 
          value={totalSubscribers} 
          subValue="Отток: —"
          icon={TrendingUp} 
        />
      </div>

      {/* Middle Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListCard 
          title={listTitle} 
          total={listTotal}
          subtitle={listSubtitle}
          items={listItems}
        />
        <EmptyCard 
          title="Топ тегов запуска бота" 
          rightMetric="0"
          subtitle="Всего пользователей"
        />
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
         <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface rounded-xl p-5 border border-white/5 h-[280px] flex flex-col"
          >
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-200 mb-3">Подписчики по пригласительным ссылкам</h3>
              <Select 
                defaultValue="channel" 
                className="w-full custom-select-dark"
                variant="borderless"
                classNames={{ popup: 'custom-select-popup' }}
              >
                 <Select.Option value="channel">Канал</Select.Option>
              </Select>
            </div>
            <div className="flex-1 bg-white/[0.02] rounded-lg border border-white/5 border-dashed" />
         </motion.div>

         <motion.div 
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface rounded-xl p-5 border border-white/5 h-[280px] flex flex-col relative"
          >
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-200 mb-3">Взаимодействия по тегам бота</h3>
              <Select 
                placeholder="Выберите бота" 
                className="w-full custom-select-dark"
                variant="borderless"
                classNames={{ popup: 'custom-select-popup' }}
              >
                 {/* Options */}
              </Select>
            </div>
            <div className="flex-1 bg-white/[0.02] rounded-lg border border-white/5 border-dashed" />
            
            <div className="absolute bottom-5 right-5">
              <button className="px-3 py-1.5 bg-surfaceLight rounded-lg text-xs font-medium text-slate-300 flex items-center gap-2 hover:text-white transition-colors">
                <Calendar size={14} />
                Все время
              </button>
            </div>
         </motion.div>
      </div>
    </div>
  );
}
